import React, { useState, useEffect, useRef } from "react";
import { IonButton, IonCard, IonCardContent, IonContent, IonImg, IonItem, IonLabel, IonModal } from "@ionic/react";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import { Helmet } from "react-helmet";

const letterImageMapping: Record<string, string> = {
    A: "apple.png",
    B: "ball.png",
    C: "cat.png",
    D: "dog.png",
    E: "elephant.png",
    F: "fish.png",
    G: "grapes.png",
    H: "hat.png",
    I: "icecream.png",
    J: "jug.png",
    K: "kite.png",
    L: "lion.png",
    M: "monkey.png",
    N: "nest.png",
    O: "orange.png",
    P: "parrot.png",
    Q: "queen.png",
    R: "rabbit.png",
    S: "sun.png",
    T: "tiger.png",
    U: "umbrella.png",
    V: "van.png",
    W: "watch.png",
    X: "xylophone.png",
    Y: "yak.png",
    Z: "zebra.png"
};

const MatchObjectsToLetter: React.FC = () => {
    const [currentLetter, setCurrentLetter] = useState<string>("");
    const [options, setOptions] = useState<string[]>([]);
    const [message, setMessage] = useState<string>("");
    const pdfRef = useRef<HTMLDivElement>(null);


    const [showImage, setShowImage] = useState(false);
    const modal = useRef<HTMLIonModalElement>(null);

    useEffect(() => {
        generateNewQuestion();
        document.title = "Letter from Object – Nursery English Worksheet | Mama’s Guidance";
    }, []);

    const generateNewQuestion = () => {
        setMessage("");
        const letters = Object.keys(letterImageMapping);
        const selectedLetter = letters[Math.floor(Math.random() * letters.length)];
        const correctImage = letterImageMapping[selectedLetter];

        const otherImages = Object.values(letterImageMapping)
            .filter(img => img !== correctImage)
            .sort(() => 0.5 - Math.random())
            .slice(0, 3);

        setCurrentLetter(selectedLetter);
        setOptions([correctImage, ...otherImages].sort(() => 0.5 - Math.random()));
    };

    const handleSelection = (selectedImage: string) => {
        if (letterImageMapping[currentLetter] === selectedImage) {
            setShowImage(true);
            setTimeout(() => {
                generateNewQuestion();
                setShowImage(false);
            }, 5000);
        } else {
            setMessage("❌ Try Again!");
        }
    };

    const waitForImagesToLoad = (element: HTMLElement): Promise<void> => {
        const images = element.querySelectorAll("img");
        const promises: Promise<void>[] = [];

        images.forEach((img) => {
            if (!img.complete) {
                promises.push(
                    new Promise((resolve) => {
                        img.onload = () => resolve();
                        img.onerror = () => resolve(); // still resolve even if image fails
                    })
                );
            }
        });

        return Promise.all(promises).then(() => undefined);
    };

    const downloadPdf = async () => {
        const input = pdfRef.current;
        if (!input) return;

        // ✅ Wait for all images inside the div to load
        await waitForImagesToLoad(input);

        const canvas = await html2canvas(input, { useCORS: true });
        const imgData = canvas.toDataURL("image/png");

        const pdf = new jsPDF();
        const imgProps = pdf.getImageProperties(imgData);
        const pdfWidth = pdf.internal.pageSize.getWidth();
        const pdfHeight = (imgProps.height * pdfWidth) / imgProps.width;

        pdf.addImage(imgData, "PNG", 0, 0, pdfWidth, pdfHeight);
        pdf.save("downloaded-content.pdf");
    };

    return (
        <>
            <Helmet>
                <meta name="description" content='Discover the "Letter from Object" activity on Mamas Guidance—an engaging exercise for nursery children to identify the starting letter of everyday objects, boosting alphabet recognition and early language development.' />
            </Helmet>
            <div className="contentPart additionWord matchImage letterOption match-container objectLetter" style={{ textAlign: "center", padding: "20px" }}>
                {
                    showImage === false ?
                        <>
                            <div className="showInfo" style={{ width: '80%' }} onClick={() => modal.current?.present()}><i className="fas fa-info-circle"></i></div>
                            <IonModal
                                ref={modal}
                                trigger="open-modal"
                                initialBreakpoint={1}
                                breakpoints={[0, 0.5, 0.75, 1]}
                                handleBehavior="cycle"
                                backdropDismiss={false}  // ✅ Prevents click-to-close
                                className="full-page-modal"
                            >
                                <IonContent className="ion-padding">
                                    <div style={{ textAlign: 'right', fontSize: '25px' }} onClick={() => modal.current?.dismiss()}><i className="fas fa-3a fa-times"></i></div>
                                    <h1>Letter from Object</h1>

                                    <p>
                                        Help your little one sharpen their alphabet and sound recognition skills with this interactive <strong>Letter from Object</strong> worksheet! In this activity, children will look at everyday pictures and identify the <strong>first letter of each object</strong>. It’s a perfect introduction to early reading and phonics for nursery students.
                                    </p>

                                    <h2>🧩 What’s Inside the Worksheet?</h2>
                                    <p>
                                        The worksheet features colorful images like <b><em>apple, sun, ball, cat,</em></b> etc. Children are asked to say the object aloud and write or choose the letter that the word begins with. This reinforces beginning sounds and alphabet familiarity.
                                    </p>

                                    <h2>🎯 Learning Goals</h2>
                                    <ul>
                                        <li>Identify the <strong>first letter</strong> of spoken words</li>
                                        <li>Strengthen phonemic awareness</li>
                                        <li>Develop letter-sound correspondence</li>
                                        <li>Enhance <strong>vocabulary</strong> and <strong>visual recognition</strong></li>
                                    </ul>

                                    <h2>👩‍🏫 How to Use</h2>
                                    <ul>
                                        <li>Point to each object and ask: <strong>“What is this?”</strong></li>
                                        <li>Say the word slowly and emphasize the beginning sound</li>
                                        <li>Let the child select or write the starting letter</li>
                                        <li>Repeat the exercise for 5–10 objects per session</li>
                                    </ul>


                                    <h2>🔗 Related Topics</h2>
                                    <div>
                                        <IonItem routerLink="/nursery/english-language/match-object-with-letter"
                                            routerDirection="none"
                                            lines="none"
                                            detail={true}>
                                            <IonLabel>Match Object With Letter</IonLabel>
                                        </IonItem>
                                        <IonItem routerLink="/nursery/english-language/circle-correct-letter"
                                            routerDirection="none"
                                            lines="none"
                                            detail={true}>
                                            <IonLabel>Circle Correct Letter</IonLabel>
                                        </IonItem>
                                        <IonItem routerLink="/nursery/english-language/before-after-between-letters"
                                            routerDirection="none"
                                            lines="none"
                                            detail={true}>
                                            <IonLabel>Before / After / Between Letters</IonLabel>
                                        </IonItem>
                                    </div>

                                    <h2>🧠 Why It Matters</h2>
                                    <p>
                                        Recognizing the first sound in a word is a vital early literacy skill. It helps children learn how words are formed and <strong>builds confidence</strong> in reading. With this worksheet, your child learns by observing, speaking, and writing — all at once!
                                    </p>

                                    <div className="btn hero-btn" onClick={() => modal.current?.dismiss()}>Close</div>
                                </IonContent>
                            </IonModal>
                            <article className="visually-hidden">
                                <h1>Letter from Object</h1>

                                <p>
                                    Help your little one sharpen their alphabet and sound recognition skills with this interactive <strong>Letter from Object</strong> worksheet! In this activity, children will look at everyday pictures and identify the <strong>first letter of each object</strong>. It’s a perfect introduction to early reading and phonics for nursery students.
                                </p>

                                <h2>🧩 What’s Inside the Worksheet?</h2>
                                <p>
                                    The worksheet features colorful images like <b><em>apple, sun, ball, cat,</em></b> etc. Children are asked to say the object aloud and write or choose the letter that the word begins with. This reinforces beginning sounds and alphabet familiarity.
                                </p>

                                <h2>🎯 Learning Goals</h2>
                                <ul>
                                    <li>Identify the <strong>first letter</strong> of spoken words</li>
                                    <li>Strengthen phonemic awareness</li>
                                    <li>Develop letter-sound correspondence</li>
                                    <li>Enhance <strong>vocabulary</strong> and <strong>visual recognition</strong></li>
                                </ul>

                                <h2>👩‍🏫 How to Use</h2>
                                <ul>
                                    <li>Point to each object and ask: <strong>“What is this?”</strong></li>
                                    <li>Say the word slowly and emphasize the beginning sound</li>
                                    <li>Let the child select or write the starting letter</li>
                                    <li>Repeat the exercise for 5–10 objects per session</li>
                                </ul>


                                <h2>🔗 Related Topics</h2>
                                <div>
                                    <IonItem routerLink="/nursery/english-language/match-object-with-letter"
                                        routerDirection="none"
                                        lines="none"
                                        detail={true}>
                                        <IonLabel>Match Object With Letter</IonLabel>
                                    </IonItem>
                                    <IonItem routerLink="/nursery/english-language/circle-correct-letter"
                                        routerDirection="none"
                                        lines="none"
                                        detail={true}>
                                        <IonLabel>Circle Correct Letter</IonLabel>
                                    </IonItem>
                                    <IonItem routerLink="/nursery/english-language/before-after-between-letters"
                                        routerDirection="none"
                                        lines="none"
                                        detail={true}>
                                        <IonLabel>Before / After / Between Letters</IonLabel>
                                    </IonItem>
                                </div>

                                <h2>🧠 Why It Matters</h2>
                                <p>
                                    Recognizing the first sound in a word is a vital early literacy skill. It helps children learn how words are formed and <strong>builds confidence</strong> in reading. With this worksheet, your child learns by observing, speaking, and writing — all at once!
                                </p>
                            </article>
                            <div ref={pdfRef}>
                                <h3>Which object starts with the letter &quot;{currentLetter}&quot;?</h3>
                                <div className="mt-5 mb-4" style={{ display: "flex", justifyContent: "center", gap: "15px", flexWrap: "wrap" }}>
                                    {options.map((img, index) => (
                                        <div key={index} onClick={() => handleSelection(img)}>
                                            <IonImg
                                                src={process.env.REACT_APP_API_URL + img}
                                                alt={img}
                                                style={{ width: "100px", height: "100px" }}
                                            />
                                        </div>
                                    ))}
                                </div>
                            </div>
                            <h3>{message}</h3>
                        </>
                        :
                        <>
                            <div className="contentPart showMiddle">
                                <img style={{ height: '150px' }} className='img-fluid' src={process.env.REACT_APP_WELDONE_LINK} />
                            </div>
                        </>
                }
            </div>

        </>
    );
};

export default MatchObjectsToLetter;
